/**
 * The summary the Toolbox path posts to a FogBugz case (epic PI-SPEC-SW-E307,
 * ER-1663, contract fogbugz 1).
 *
 * One pure function, used by both the SpecTree web review step (preview) and
 * the SpecTree API (the posted attachment), so the person posts exactly what
 * they reviewed. The title, sections and `## Digest` heading are laid out as
 * in the website's server-side summary (`composeSummaryMarkdown` in the
 * SpecTree API's `services/evtxSummaryService.ts`); only the provenance line
 * differs: it names the person whose Claude wrote the sections, with markdown
 * characters in the name escaped.
 */
import { FOGBUGZ_EVTX_PROVENANCE } from "./constants.js";
/**
 * `text` with the characters that change inline markdown (backslash,
 * backtick, `*`, `_`, `[`, `]`, `<`, `>`, `#`, `~`, `|`) backslash-escaped, so
 * a person's name such as `j_smith` or `Pat *Lee*` reads as typed inside the
 * italic provenance line. Other punctuation (`O'Brien`, `Mary-Jane`) is left
 * alone, since the FogBugz event text is also read raw.
 */
function escapeMarkdownText(text) {
    return text.replace(/[\\`*_[\]<>#~|]/g, "\\$&");
}
/**
 * `text` as one line: line breaks and other control characters become
 * spaces, so a name from FogBugz (an attachment name is the uploader's
 * choice) cannot start a heading or section of its own. Not escaped: in the
 * one heading line it lands in, nothing else can open a block, and the
 * FogBugz event text is read raw (`Toro\_Irrigation` would show the
 * backslash). Used for the summary's title by both summary paths.
 */
export function markdownOneLine(text) {
    return text
        // eslint-disable-next-line no-control-regex -- control characters are exactly what is removed
        .replace(/[\u0000-\u001f\u007f\u2028\u2029]+/g, " ")
        .replace(/ {2,}/g, " ")
        .trim();
}
/** `sections` without leading blank lines or trailing space; the first line keeps its indentation (it may be a fence). */
function trimBlankLines(sections) {
    return sections.replace(/^(?:[ \t]*(?:\r\n|\r|\n))+/, "").trimEnd();
}
/**
 * The full summary markdown:
 *
 * ```
 * # Event log summary: <fileName>
 *
 * _Written by <personName, escaped>'s Claude from the parsed digest; verify against the attached .evtx._
 *
 * <sections>
 *
 * ## Digest
 *
 * <digestMarkdown>
 * ```
 *
 * Sections are kept verbatim apart from leading blank lines and trailing
 * space: the first line keeps its indentation, so text the heading check
 * accepted cannot turn into an open code fence here.
 */
export function composeToolboxEvtxSummary(input) {
    const parts = [
        `# Event log summary: ${markdownOneLine(input.fileName)}`,
        "",
        `_${FOGBUGZ_EVTX_PROVENANCE(escapeMarkdownText(markdownOneLine(input.personName)))}_`,
        "",
        trimBlankLines(input.sections),
        "",
        "## Digest",
        "",
        input.digestMarkdown,
    ];
    return parts.join("\n");
}
//# sourceMappingURL=compose.js.map